/**
 * Search History Management
 *
 * Manages user search history for universal search functionality.
 * Provides persistent storage and retrieval of search terms with deduplication.
 */

import { loadState, saveState } from "shared/localstorage_helpers"

const searchHistoryKey = (userId?: string) => `search_history:${userId}`
export const MINIMUM_SEARCH_TERM_LENGTH = 4
const maxSearchHistoryLength = 10

/**
 * Load User Recent Searches
 *
 * Retrieves the user's search history from localStorage.
 * Returns an empty array if no user is logged in or no history exists.
 *
 * @returns Array of search entries in chronological order (newest first)
 */
export const loadUserRecentSearches = () => {
  if (!window.App.user?.id) return []

  return loadState(searchHistoryKey(window.App.user.id)) || []
}

export interface SearchEntryInterface {
  type: string
  term: string
  /** Optional service details (only for service type entries) */
  details?: {
    name?: string
    brand_color?: string
    module_name?: string
    image?: string
  }
}

/**
 * Save User Search
 *
 * Adds a new search entry to the user's search history with automatic deduplication.
 * Validates input requirements, manages storage limits, and dispatches update events.
 *
 * Deduplication Process:
 * 1. Adds new entry to the beginning of history
 * 2. Removes any existing entries with the same term (keeps newest)
 * 3. Applies maximum length limit
 * 4. Saves to localStorage and dispatches update event
 *
 * @param type - Entry type: "search" for queries, "service" for service selections
 * @param term - The search term or service name (minimum 4 characters)
 * @param details - Optional service metadata (for service type entries)
 * @returns Updated search history array
 */
export const saveUserSearch = ({ type, term, details }: SearchEntryInterface) => {
  if (!window.App.user?.id || term === "" || term.length < MINIMUM_SEARCH_TERM_LENGTH) return []
  return saveSearchToHistory({ type, term, details })
}

export const saveSearchTermBypassLength = ({ type, term, details }: SearchEntryInterface) => {
  if (!window.App.user?.id || term === "") return []
  return saveSearchToHistory({ type, term, details })
}

const saveSearchToHistory = ({ type, term, details }: SearchEntryInterface) => {
  const historyKey = searchHistoryKey(window.App.user.id)

  const entry = {
    type,
    term,
    details,
  }

  let history = loadUserRecentSearches()
  if (history.length === 0) {
    history.push(entry)
  } else {
    history.unshift(entry)
    history = removeDuplicateEntries(history).slice(0, maxSearchHistoryLength)
  }

  saveState(history, historyKey)

  dispatchEvent(new CustomEvent("search-history:updated", {}))

  return history
}

const removeDuplicateEntries = (history: SearchEntryInterface[]) => {
  // Remove duplicate entries by term, with service type taking precedence over search type
  const termMap = new Map<string, SearchEntryInterface>()

  history.forEach((entry: SearchEntryInterface) => {
    const lowerTerm = entry.term.toLowerCase()
    const existingEntry = termMap.get(lowerTerm)
    if (!existingEntry) {
      termMap.set(lowerTerm, entry)
    } else {
      // Service type has precedence over search type
      if (entry.type === "service" && existingEntry.type === "search") {
        // Replace search with service
        termMap.set(lowerTerm, entry)
      }

      // Otherwise, do nothing - keep existing
    }
  })

  // Convert back to array maintaining chronological order (newest first)
  return Array.from(termMap.values())
}

export const createSearchItemFromElement = (element: HTMLAnchorElement) => {
  if (element.dataset.moduleName) {
    return {
      type: "service",
      term: element.dataset.term,
      details: {
        name: element.dataset.term,
        brand_color: element.dataset.brandColor,
        module_name: element.dataset.moduleName,
        image: element.dataset.image,
      },
    }
  } else {
    return {
      type: "search",
      term: element.dataset.term,
    }
  }
}
